import { styled, Tab, Tabs } from "@mui/material";
import { Box, SxProps } from "@mui/system";
import { TabPanel } from "components/atomics/TabPanel";
import { selectionModelAtom } from "pages/AssetManagement/Assets/recoilStates";
import { useEffect, useMemo, useState } from "react";
import { Link, useMatches } from "react-router-dom";
import { useResetRecoilState } from "recoil";
import projectColors from "themes/projectColors";
export type RenderElementsStart = {
  label: React.ReactNode;
  content?: React.ReactNode;
  To?: string | undefined;
  totalCount?: number;
  showTotlaCount?: boolean;
}[];

export interface CustomHorizontalTabsProps {
  renderElementsStart?: RenderElementsStart;
  renderElementsEnd?: React.ReactElement[];
  sx?: SxProps;
  loading?: boolean;
}
const StyledTotalCount = styled("span")<{ active: boolean }>(({ theme, active }) => ({
  backgroundColor: active ? projectColors.functional.info100 : "#757575",
  color: active ? projectColors.primary.main : projectColors.functional.white,
  borderRadius: "8px",
  padding: active ? "4px 8px 4px 8px" : "3px 7px 2px 7px",
  fontSize: "12px",
  fontWeight: "bold",
}));
const StyledLabel = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const StyledHorizontalTabbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid",
  borderBottomColor: projectColors.functional.divider,
  background: "none",
}));

const StyledBox = styled(Box)(({ theme }) => ({
  width: "calc(100% - 74px)",
  margin: "0px 0px 0px 2em",
  backgroundColor: theme.palette.background.default,
}));

const useFindMostSpecificPath = (paths: (string | undefined)[]) => {
  const matches = useMatches();
  return useMemo(
    () =>
      paths.reduce<{ activeIndex: number; path: string }>(
        (agg, elem, currentIndex) => {
          const match = matches.some(({ pathname }) => pathname === elem);
          if ((match && elem?.length) ?? 0 > agg.path.length) return { activeIndex: currentIndex, path: elem ?? "" };
          return agg;
        },
        { activeIndex: 0, path: "" }
      ),
    [matches, paths]
  );
};

const CustomHorizontalTabs: React.FC<CustomHorizontalTabsProps> = ({ renderElementsStart, renderElementsEnd, sx }) => {
  const [value, setValue] = useState<number>(0);
  const setResetSelectionModel = useResetRecoilState(selectionModelAtom);
  const { activeIndex } = useFindMostSpecificPath(renderElementsStart?.map(({ To }) => To) ?? []);
  useEffect(() => {
    setValue(activeIndex);
    setResetSelectionModel();
  }, [activeIndex, setResetSelectionModel]);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const matchIndex = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  };
  return (
    <StyledBox sx={sx}>
      <StyledHorizontalTabbar>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          {renderElementsStart?.map(({ label, To, totalCount, showTotlaCount }, index) =>
            To === undefined ? (
              <Tab
                disableRipple
                style={{ minWidth: "49px", paddingLeft: 0, paddingRight: 0, marginRight: 20, paddingTop: 0 }}
                key={index}
                label={label}
                {...matchIndex(index)}
              />
            ) : (
              <Tab
                disableRipple
                style={{
                  minWidth: "49px",
                  paddingLeft: 10,
                  paddingRight: 0,
                  marginRight: 20,
                  fontSize: "14px",
                }}
                key={index}
                label={
                  <StyledLabel>
                    <span>{label}</span>
                    {showTotlaCount && (
                      <StyledTotalCount active={value === index}>
                        {totalCount && totalCount > 99 ? "99+" : totalCount}
                      </StyledTotalCount>
                    )}
                  </StyledLabel>
                }
                component={Link}
                to={To}
                {...matchIndex(index)}
              />
            )
          )}
        </Tabs>
        <Box sx={{ display: "flex" }}>{renderElementsEnd?.map((el) => el)}</Box>
      </StyledHorizontalTabbar>

      {renderElementsStart?.map(({ content }, index) => (
        <TabPanel key={index} value={value} index={index}>
          {content}
        </TabPanel>
      ))}
    </StyledBox>
  );
};

export default CustomHorizontalTabs;
